<template id="configuration-tree">
	<div
		v-if="
			parentChildren['root'] !== undefined && factorConfigurations.length !== 0
		"
	>
		<div
			v-for="(crumb, index) in breadcrumbItems"
			:key="index"
			class="breadcrumb-bar"
		>
			<v-btn text @click="goBackToParent(crumb, index, false)">
				<template v-if="AFM[crumb].treeMode == 'Root'">
					<v-icon class="breadcrumb-bar-icon"
						>mdi-folder-multiple-outline</v-icon
					>
				</template>
				<template v-else-if="AFM[crumb].treeMode == 'Parent'">
					<v-icon class="breadcrumb-bar-icon"
						>mdi-folder-outline</v-icon
					>
				</template>
				{{ AFM[crumb].label }}
			</v-btn>
		</div>
		<v-data-table
			:headers="headers"
			:items="items"
			:expanded.sync="expanded"
			item-key="factorId"
			:itemsPerPage="100"
			mobile-breakpoint="0"
			hide-default-header
			hide-default-footer
			show-expand
		>
			<template v-slot:item.label="{ item }">
				<div class="configuration-tree-row-header">
					<div class="configuration-tree-row-label">
						<template v-if="item.treeMode == 'Root'">
							<v-icon>mdi-folder-multiple-outline</v-icon>
						</template>
						<template v-else-if="item.treeMode == 'Parent'">
							<v-icon>mdi-folder-outline</v-icon>
						</template>
						<p class="configuration-tree-row-label-text">
							{{ item.label }}
						</p>
					</div>
					<div class="configuration-tree-row-actions">
						<!-- First Layer -->
						<div
							v-if="item.treeMode === 'Root'"
							class="configuration-tree-action-buttons configuration-tree-root"
						>
							<addFactorOrGroupDialog
								:item="item"
								:expanded="expanded"
								:groups="true"
								:type="item.type"
								@addAuditMessage="AddAuditLogMessage"
								@changeParent="changeParent"
							/>
						</div>

						<!-- Second Layer -->
						<template v-else-if="item.treeMode === 'Parent'">
							<div class="configuration-tree-action-buttons configuration-tree-parent">
								<addFactorOrGroupDialog
									:item="item"
									:expanded="expanded"
									:groups="false"
									:type="item.type"
									@addAuditMessage="AddAuditLogMessage"
								/>
								<v-menu offset-y>
									<template v-slot:activator="{ on, attrs }">
										<v-btn text
											class="configuration-tree-action-button-menu"
											v-bind="attrs"
											v-on="on"
											title="menu"
											label="menu"
										>
											<v-icon>mdi-dots-vertical</v-icon>
										</v-btn>
									</template>
									<!-- Delete dialog -->
									<template
										v-if="
											parentChildrenMap[item.factorId] !==
												undefined &&
											parentChildrenMap[item.factorId].length !==
												0
										"
									>
										<modal-dialog
											title="Are you sure?"
											:executeAction="
												function () {
													deleteFactorOrGroup(item);
												}
											"
											executeText="Delete Group"
										>
											<div slot="activator">
												<v-btn class="dangerous">Delete</v-btn>
											</div>
											<div slot="content">
												<p>
													Are you sure you want to delete the
													{{ item.label }} group? This will
													delete its
													{{
														parentChildrenMap[item.factorId]
															.length
													}}
													child
													{{
														parentChildrenMap[item.factorId]
															.length == 1
															? "factor"
															: "factors"
													}}
													as well!
												</p>
											</div>
										</modal-dialog>
									</template>
									<template v-else>
										<v-btn
											class="dangerous"
											@click="deleteFactorOrGroup(item)"
											>Delete</v-btn
										>
									</template>
								</v-menu>
							</div>
						</template>

						<!-- Third Layer -->
						<template v-else>
							<v-menu offset-y>
								<template v-slot:activator="{ on, attrs }">
									<div class="configuration-tree-action-buttons configuration-tree-child">
										<v-btn text
											class="configuration-tree-action-button-menu"
											v-bind="attrs"
											v-on="on"
											title="menu"
											label="menu"
										>
											<v-icon>mdi-dots-vertical</v-icon>
										</v-btn>
									</div>
								</template>
								<v-btn
									class="dangerous"
									@click="deleteFactorOrGroup(item)"
									>Delete</v-btn
								>
							</v-menu>
						</template>
					</div>
				</div>
			</template>

			<template v-slot:expanded-item="{ headers, item }">
				<td :colspan="headers.length">
					<template>
						<!--tables loops over factors as an 'item'-->
						<div class="factor-details">
							<div class="supporting-text">
								<div
									class="description"
									v-if="item.description"
								>
									<h3>Description</h3>
									<p>{{ item.description }}</p>
								</div>
								<div class="whyitworks" v-if="item.whyItWorks">
									<h3>Why It Works</h3>
									<p>{{ item.whyItWorks }}</p>
								</div>
								<div class="knowledge-base-url" v-if="item.knowledgeBaseUrl">
									<a :href="item.knowledgeBaseUrl" target="_blank">Learn More</a>
								</div>
							</div>
							<v-form
								v-model="currentThresholdsValid"
								class="thresholds"
							>
								<div
									class="threshold-item"
									v-for="threshold in item.thresholds"
									:key="threshold.id"
								>
									<div class="threshold-label">
										{{ threshold.label }}
									</div>
									<div class="threshold-levels">
										<div
											v-for="(inputField,
											index) in threshold.levels
												.inputFields"
											:key="index"
											class="threshold-levels-loop"
										>
											<!-- only show these labels if there are multiple levels... no ALL -->
											<template
												v-if="
													threshold.levels.inputFields
														.length > 1
												"
											>
												<v-chip
													:class="inputField.fieldId"
													:pill="true"
													:title="inputField.fieldId"
													>{{
														inputField.fieldId
															.charAt(0)
															.toUpperCase()
													}}</v-chip
												>
											</template>

											<v-text-field
												class="threshold-level-item"
												v-model="inputField.fieldValue"
												:label="threshold.placeholder"
												:outlined="!isReadOnly"
												:readonly="isReadOnly"
												:rules="[
													validateField(
														inputField.fieldValue,
														threshold.validateRegex
													),
													validateRelativeThresholds(threshold)
												]"
												:type="
													setType(
														threshold.levels.type
													)
												"
												@change="thresholdChanged(item.label)"
												required
											></v-text-field>
										</div>
									</div>
								</div>
							</v-form>
						</div>
					</template>
					<template
						v-if="
							(parentChildren[item.factorId] === undefined || parentChildren[item.factorId].length === 0) &&
							item.treeMode !== 'Child'
						"
					>
						<div class="group-with-no-children">
							<p>Groups must contain at least one factor...</p>
							<span
								v-if="item.treeMode === 'Root'"
								class="configuration-tree-action-buttons configuration-tree-root"
							>
								<addFactorOrGroupDialog
									:item="item"
									:expanded="expanded"
									:groups="true"
									:type="item.type"
									@addAuditMessage="AddAuditLogMessage"
									@changeParent="changeParent"
								/>
							</span>
							<template v-if="item.treeMode === 'Parent'">
								<span class="configuration-tree-action-buttons configuration-tree-parent">
									<addFactorOrGroupDialog
										:item="item"
										:expanded="expanded"
										:groups="false"
										:type="item.type"
										@addAuditMessage="AddAuditLogMessage"
									/>
								</span>
								<p>or</p>
								<v-btn class="dangerous" @click="deleteFactorOrGroup(item)">Delete Group</v-btn>
							</template>
						</div>
					</template>
					<template v-else>
						<div
							v-for="(child, index) in parentChildren[
								item.factorId
							]"
							:key="index"
							class="display-children"
						>
							<div class="display-children-label">
								<template v-if="child.treeMode == 'Root'">
									<v-icon>mdi-folder-multiple-outline</v-icon>
								</template>
								<template
									v-else-if="child.treeMode == 'Parent'"
								>
									<v-icon>mdi-folder-outline</v-icon>
								</template>
								<p class="display-chilren-label-text">
									{{ child.label }}
								</p>
							</div>
							<div class="display-children-action-buttons">
								<v-btn text @click="changeParent(child)">
									View &amp; Edit
									<v-icon
										class="display-children-action-buttons-right-arrow"
										>mdi-arrow-right</v-icon
									>
								</v-btn>
								<v-menu offset-y>
									<template v-slot:activator="{ on, attrs }">
										<v-btn text
											v-bind="attrs"
											v-on="on"
											title="menu"
											label="menu"
										>
											<v-icon>mdi-dots-vertical</v-icon>
										</v-btn>
									</template>
									<!-- Delete dialog -->
									<template
										v-if="
											parentChildrenMap[
												child.factorId
											] !== undefined
										"
									>
										<modal-dialog
											title="Are you sure?"
											:executeAction="
												function () {
													deleteFactorOrGroup(child);
												}
											"
											executeText="Delete"
										>
											<div slot="activator">
												<v-btn class="dangerous"
													>Delete</v-btn
												>
											</div>
											<div slot="content">
												<p>
													Are you sure you want to
													delete the
													{{ child.label }} group?
													This will delete its
													{{
														parentChildrenMap[
															child.factorId
														].length
													}}
													child
													{{
														parentChildrenMap[
															item.factorId
														].length == 1
															? "factor"
															: "factors"
													}}
													as well!
												</p>
											</div>
										</modal-dialog>
									</template>
									<template v-else>
										<v-btn
											class="dangerous"
											@click="deleteFactorOrGroup(child)"
											>Delete</v-btn
										>
									</template>
								</v-menu>
							</div>
						</div>
					</template>
				</td>
			</template>
		</v-data-table>
	</div>
	<div v-else>
		<v-skeleton-loader type="table"></v-skeleton-loader>
	</div>
</template>

<script>
	/**
	 * @fileoverview configuration-tree.vue displays a profile's factor configurations in a tree structure.
	 * It relies on vuex to map the state variables from the store files.
	 * 
	 * @components
	 * 		addFactorOrGroupDialog - dialog box for adding a factor or group to the factor configuration
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */
	import { mapState } from "vuex";

	export default {
		props: {
			isReadOnly: Boolean,	// modifiable flag
		},
		data() {
			return {
				expanded: [],	// list of expanded items
				singleExpand: false,	// true if only one item is allowed to be expanded at once
				breadcrumbItems: [],	// list of bread crumb ids
				parentId: "root",		// id of the item's parent
				currentThresholdsValid: false,	// validation flag
				/**
				 * Headers for the configuration tree items
				 */
				headers: [
					{ text: "", value: "data-table-expand" },
					{
						text: "",
						align: "start",
						sortable: false,
						value: "label",
					},
				],
			};
		},
		computed: {
			...mapState({
				parentChildren: (state) => state.config.parentChildrenMap,
				AFM: (state) => state.config.availableFactorListMap,
				factorConfigurations: (state) => state.config.factorConfigurations,
				factorConfigMap: (state) => state.config.factorConfigMap,
				parentChildrenMap: (state) => state.config.parentChildrenMap,
				allowSetConfigProgress: (state) =>
					state.config.editingTreeDepth === 0,
				loggedInUser: (state) => state.account.loggedInUser,
			}),
			/**
			 * Get the factor configuration for the parent
			 * @returns the factor config object
			 */
			items() {
				if (this.parentId === "root") {
					return this.parentChildren["root"];
				}
				return this.factorConfigMap[this.parentId];
			},
			/**
			 * Getter and setter for the current configuration valid flag
			 */
			currentConfigurationValid: {
				get() {
					let valid = false;

					if (this.parentChildrenMap[this.parentId]) {
						if ((this.parentChildrenMap[this.parentId].length > 0) &&
						(this.parentChildrenMap["FriendlyFraud"].length > 0) &&
						(this.parentChildrenMap["TrueFraud"].length > 0)) {
							valid = true;
						}
					} else if (this.AFM[this.parentId].treeMode === "Child") {
						valid = true;
					}
	
					return valid;				
				},
				set(val) {
					this.$store.dispatch("setConfigurationValid", val);
				}

			}
		},
		watch: {
			/**
			 * Update the currentConfigurationValid flag based on the parentId
			 */
			parentId() {
				let valid = false;

				if (this.parentId === "root") {
					if ((this.parentChildrenMap["FriendlyFraud"].length > 0) && (this.parentChildrenMap["TrueFraud"].length > 0)) {
						valid = true;
					}
				} else if (this.AFM[this.parentId].treeMode === "Child") {
					valid = true;
				} else if (this.parentChildrenMap[this.parentId]) {
					if (this.parentChildrenMap[this.parentId].length > 0) {
						valid = true;
					}
				}

				this.currentConfigurationValid = valid;
			},
			/**
			 * Update the thresholdFormValid flag in the config store
			 */
			currentThresholdsValid() {
				this.$store.dispatch(
					"setThresholdFormValid",
					this.currentThresholdsValid
				);
			},
			/**
			 * Expand True and Friendly Fraud configurations when they are on the screen
			 */
			items() {
				if (this.items === this.parentChildren["root"]) {
					this.expanded = this.parentChildren["root"];
				}
			}
		},
		components: {
			addFactorOrGroupDialog: () =>
				import("@/components/add-factor-or-group-dialog.vue"),
		},
		methods: {
			/**
			 * Navigate to the child factor
			 * Add parentId to the breadcrumbItems
			 * @param {Object} child - selected factor to display
			 */
			changeParent(child) {
				// Change parent/navigate, only if the current screen is valid
				if (this.currentConfigurationValid && this.currentThresholdsValid) {
					this.parentId = child.factorId;
					this.breadcrumbItems.push(child.parentId);
					this.$store.dispatch(
						"setEditingTreeDepth",
						this.breadcrumbItems.length
					);
					this.expanded = [child];
				} else {
					const notification = {
						status: "error",
						message: "Fix missing or invalid settings before leaving.",
					};
					this.$store.dispatch(
						"notifications/addNotification",
						notification
					);
				}
			},
			/**
			 * Navigate back to the selected breadcrumb
			 * @param {String} crumb - factorId for the selected breadcrumb
			 * @param {Number} index - index of the breadcrumb within the breadcrumbItems list
			 * @param {Boolean} force - true if forcing navigation 
			 */
			goBackToParent(crumb, index, force) {
				// Change parent/navigate, only if the current screen is valid or force is true
				if ((this.currentConfigurationValid && this.currentThresholdsValid) || force) {
					if (crumb == "TrueFraud" || crumb == "FriendlyFraud") {
						this.parentId = "root";
					} else {
						this.parentId = crumb;
					}

					this.breadcrumbItems.splice(
						index,
						this.breadcrumbItems.length - index
					);

					this.$store.dispatch(
						"setEditingTreeDepth",
						this.breadcrumbItems.length
					);

					// expand the target parent...
					this.expanded.push(
						this.items.find((item) => item.factorId === crumb)
					);
				} else {
					const notification = {
						status: "error",
						message: "Fix missing or invalid settings before leaving.",
					};
					this.$store.dispatch(
						"notifications/addNotification",
						notification
					);
				}
			},
			/**
			 * Delete a factor or group:
			 * 		Remove factor or group from parentChildrenMap
			 * 		Check if deleting factor is being viewed
			 * 		Remove it's children from the parentChildrenMap
			 * 		Remove factor and children from factorConfigurations
			 * 		Remap factorConfigurations
			 * 		Update addable groups and factors list
			 * 		Add message to audit log
			 * 
			 * @param {Object} item - selected factor or group to be deleted
			 */
			deleteFactorOrGroup(item) {
				// remove factor or group from parent in parentChildrenMap
				var parent = undefined;
				if (item.parentId === undefined) {
					parent = this.breadcrumbItems[this.breadcrumbItems.length - 1];
				} else {
					parent = item.parentId;
				}

				// check if deleting factor being viewed
				if (item.factorId === this.parentId) {
					this.goBackToParent(
						this.breadcrumbItems[this.breadcrumbItems.length - 1],
						this.breadcrumbItems.length - 1,
						true
					);
				}

				var index = this.parentChildrenMap[parent].findIndex(
					(child) => child.factorId === item.factorId
				);

				this.parentChildrenMap[parent].splice(index, 1);

				// Check for a valid configuration
				if (this.parentChildrenMap[parent].length < 1) {
					const notification = {
						status: "warning",
						message: "All groups require at least one factor."
					};
					this.$store.dispatch("notifications/addNotification", notification);

					this.currentConfigurationValid = false;
				}

				// remove its children from the parentChildrenMap
				var removedChildren = undefined;
				if (this.parentChildrenMap[item.factorId] !== undefined) {
					removedChildren = this.parentChildrenMap[item.factorId];
					this.parentChildrenMap[item.factorId] = undefined;
				}

				// remove factor from factorConfigurations
				var factorIndex = this.factorConfigurations.findIndex(
					(config) =>
						config.factorConfiguration.factorId === item.factorId
				);

				this.factorConfigurations.splice(factorIndex, 1);

				if (removedChildren !== undefined) {
					removedChildren.map((child) => {
						// remove removedChildren from factorConfigurations
						var childIndex = this.factorConfigurations.findIndex(
							(config) =>
								config.factorConfiguration.factorId ===
								child.factorId
						);

						this.factorConfigurations.splice(childIndex, 1);
					});
				}

				// Remap factorConfigurations
				this.$store.dispatch("createFactorConfigMap");

				// Update addable groups and factors list
				this.$store.dispatch("createAddableGroupsAndFactors");
				this.setProfileChanged();

				// add message to auditMessages
				let message = this.loggedInUser.name + ' removed a ' + (item.treeMode === "Parent" ? 'category group: ' : 'factor: ') + item.label;
				this.AddAuditLogMessage(message);
			},
			/**
			 * Add a message to the profile audit log
			 * @param {String} message - message to be added to the audit log
			 */
			AddAuditLogMessage(message){
				// add object to the auditMessages
				this.$store.dispatch("appendProfileAuditMessages", message);
			},
			/**
			 * Validate field based on regex value
			 * @param {*} value - value to be validated. Can be multiple types
			 * @param {String} srcRegex - regex string to compare against
			 * @returns true if valid, and an invalid message if invalid
			 */
			validateField(value, srcRegex) {
				/*
				 * TODO: consider presets that can't be done as easily via regex, i.e.
				 *	futureDate, pastDate ... assuming we set the right HTML5 type, there are
				 *	attributes we can set for many checks/controls and we can feed those in
				 * 	without/in addition to this method
				 */

				/*
				 * check the given value against the given regex pattern
				 * a match returns true and means the input is valid
				 */

				return function () {
					let success = true;
					let message = undefined;

					if (!value) {
						success = false;
						message = "Incomplete or Invalid";
					} else if (srcRegex) {
						const regex = RegExp(srcRegex);
						success = regex.test(value);
						if (!success) {
							message = "Invalid Response";
						}
					}
					return message ? message : success;
				};
			},
			/**
			 * Validate relative thresholds based on orderType
			 * @param {Object} threshold - group of values for a factor
			 * @param {String} fieldId -  Id for the specific field we're validating
			 * @returns true if valid, and an invalid message if invalid
			 */
			validateRelativeThresholds(threshold) {
				let fieldMap = {}; 
				console.log(threshold.levels.inputFields);
				threshold.levels.inputFields.map(field => {
					fieldMap[field.fieldId] = field.fieldValue;
				})
				
				let thresholdArray = [];
				let low = fieldMap.low;
				let medium = fieldMap.medium;
				let high = fieldMap.high;

				if (low !== undefined) {
					thresholdArray.push(Number(low));
				}
				if (medium !== undefined) {
					thresholdArray.push(Number(medium));
				}
				if (high !== undefined) {
					thresholdArray.push(Number(high));
				}


				let invalid = false;
				switch (threshold.orderingType) {
					case "Ascending":
						var min = undefined;
						thresholdArray.map(value => {
							if (min === undefined){
								min = value;
							} else if (value <= min){
								invalid = true;
							}
							min = value;
						});
						break;

					case "AscendingEquals":
						var minEq = undefined;
						thresholdArray.map(value => {
							if (minEq === undefined){
								minEq = value;
							} else if (value < minEq){
								invalid = true;
							}
							minEq = value;
						});
						break;

					case "Descending":
						var max = undefined;
						thresholdArray.map(value => {
							if (max === undefined){
								max = value;
							} else if (value >= max){
								invalid = true;
							}
							max = value;
						});
						break;
						
					case "DescendingEquals":
						var maxEq = undefined;
						thresholdArray.map(value => {
							if (maxEq === undefined){
								maxEq = value;
							} else if (value > maxEq){
								invalid = true;
							}
							maxEq = value;
						});
						break;

					default:
						return true;
				}

				if (invalid) {
					return 'Invalid logic';
				} else {
					return true;
				}
			},
			/**
			 * Add a message to the audit log when a threshold value changes
			 * @param {String} label - label for the factor with the changed threshold value
			 */
			thresholdChanged(label) {
				// add message to auditMessages
				let message = this.loggedInUser.name + ' updated factor values: ' + label;

				this.AddAuditLogMessage(message);
				this.setProfileChanged();
			},
			/**
			 * Set profile changed to true in the config store
			 */
			setProfileChanged() {
				this.$store.dispatch("setProfileChanged", true);
			},
			/**
			 * Set field type
			 * @param {String} srcType - original field type
			 * @returns string of new type
			 */
			setType(srcType) {
				/*
				 *	TODO: see if we can just enforce during creation/entry...
				 *
				 *	https://developer.mozilla.org/en-US/docs/Learn/Forms/HTML5_input_types
				 *		text, email, search, tel, url, number, range, color
				 *		date, datetime-local, month, time, week
				 */
				let type = srcType ? srcType.toLowerCase() : "text";

				switch (type) {
					case "integer":
					case "long":
					case "decimal":
						type = "number";
						break;
					case "phone":
						type = "tel";
						break;
					default:
						break;
				}

				return type;
			},
		},
		/**
		 * Mounted lifecycle hook
		 * Expand Friendly and True Fraud
		 */
		mounted() {
			if (this.parentChildren["root"] !== undefined) {
				this.expanded = this.parentChildren["root"];
			}
		},
	};
</script>